import { useState } from "react";

import { LockOutlined } from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Flex,
	Form,
	Image,
	Input,
	Typography,
} from "antd";

import { resetPasswordWithToken } from "@neu/libui";
import type { ResetPasswordComponentProps } from "@neu/libui";

import styles from "./Login.module.scss";

const MIN_LENGTH = 8;

export function ResetPassword({
	token,
	onNavigateLogin,
	onResetSessionInvalidate,
}: ResetPasswordComponentProps) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState(false);

	const handleSubmit = async (values: {
		password: string;
		confirmPassword: string;
	}) => {
		const trimmedToken = token?.trim() ?? "";
		if (!trimmedToken) {
			return;
		}
		setLoading(true);
		setError(null);
		try {
			await resetPasswordWithToken(trimmedToken, values.password);
			onResetSessionInvalidate?.();
			setDone(true);
		} catch (err: unknown) {
			setError(
				err instanceof Error ? err.message : "Something went wrong"
			);
		} finally {
			setLoading(false);
		}
	};

	const hasToken = Boolean(token?.trim());

	return (
		<Flex justify="center" align="center" style={{ minHeight: "100vh" }}>
			<Card className={styles.loginCard}>
				<div className={styles.logoContainer}>
					<Image
						src="/logo.png"
						alt="Neu Application Logo"
						preview={false}
						className={styles.appLogo}
					/>
				</div>

				{!hasToken && (
					<Alert
						message="Invalid link"
						description="This reset link is missing a token. Request a new password reset from the sign-in page."
						type="error"
						showIcon
						style={{ marginBottom: 24, borderRadius: 8 }}
					/>
				)}

				{hasToken && error && (
					<Alert
						message="Could not reset password"
						description={error}
						type="error"
						showIcon
						closable
						onClose={() => {
							setError(null);
						}}
						style={{ marginBottom: 24, borderRadius: 8 }}
					/>
				)}

				{hasToken && done && (
					<Flex vertical gap="middle">
						<Typography.Title level={4} style={{ marginTop: 0 }}>
							Password updated
						</Typography.Title>
						<Typography.Paragraph style={{ marginBottom: 0 }}>
							You can sign in with your new password.
						</Typography.Paragraph>
						<Button
							type="primary"
							size="large"
							block
							onClick={onNavigateLogin}
						>
							Back to sign in
						</Button>
					</Flex>
				)}

				{hasToken && !done && (
					<Form
						name="reset-password"
						onFinish={(values: {
							password: string;
							confirmPassword: string;
						}) => void handleSubmit(values)}
						layout="vertical"
						autoComplete="off"
					>
						<Typography.Title level={4} style={{ marginTop: 0 }}>
							Set a new password
						</Typography.Title>
						<Form.Item
							name="password"
							rules={[
								{
									required: true,
									message: "Please enter a new password",
								},
								{
									min: MIN_LENGTH,
									message: `At least ${MIN_LENGTH} characters`,
								},
							]}
							style={{ marginBottom: 10 }}
							hasFeedback
						>
							<Input.Password
								prefix={
									<LockOutlined
										style={{
											color: "var(--color-primary-600)",
										}}
									/>
								}
								placeholder="New password"
								size="large"
								autoFocus
								className={styles.passwordInput}
							/>
						</Form.Item>
						<Form.Item
							name="confirmPassword"
							dependencies={["password"]}
							rules={[
								{
									required: true,
									message: "Please confirm your password",
								},
								({ getFieldValue }) => ({
									validator(_, value) {
										if (
											!value ||
											getFieldValue("password") === value
										) {
											return Promise.resolve();
										}
										return Promise.reject(
											new Error("Passwords do not match")
										);
									},
								}),
							]}
							style={{ marginBottom: 10 }}
							hasFeedback
						>
							<Input.Password
								prefix={
									<LockOutlined
										style={{
											color: "var(--color-primary-600)",
										}}
									/>
								}
								placeholder="Confirm new password"
								size="large"
								className={styles.passwordInput}
							/>
						</Form.Item>
						<Form.Item style={{ marginTop: 8 }}>
							<Button
								type="primary"
								size="large"
								htmlType="submit"
								loading={loading}
								block
								style={{
									borderRadius: 12,
									height: 42,
								}}
							>
								Update password
							</Button>
						</Form.Item>
						<Flex justify="center">
							<Typography.Link onClick={onNavigateLogin}>
								Back to sign in
							</Typography.Link>
						</Flex>
					</Form>
				)}

				{!hasToken && (
					<Flex justify="center" style={{ marginTop: 16 }}>
						<Button type="default" onClick={onNavigateLogin}>
							Back to sign in
						</Button>
					</Flex>
				)}
			</Card>
		</Flex>
	);
}
